import { useCallback } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { currentMonthKey, parseMonthKey } from '../../utilities/helper/date.helper';

/**
 * The month in `?month=YYYY-MM`.
 *
 * Validated on the way in, falling back to `fallback` (the current month unless a page
 * says otherwise) - pointedly unlike the
 * workspace's `?tab=`, which accepts `99` and renders a blank screen.
 *
 * Written with `replace`, so the back button leaves Finance instead of walking back
 * through every month the admin stepped past.
 */
export const useFinanceMonth = (fallback: () => string = currentMonthKey): [string, (month: string) => void] => {
  const navigate = useNavigate();
  const location = useLocation();

  const raw = new URLSearchParams(location.search).get('month');
  const month = parseMonthKey(raw) ? (raw as string) : fallback();

  const setMonth = useCallback(
    (next: string) => {
      const params = new URLSearchParams(location.search);
      params.set('month', next);
      navigate(`${location.pathname}?${params.toString()}`, { replace: true });
    },
    [navigate, location.pathname, location.search]
  );

  return [month, setMonth];
};
